import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { showErrorToast, showSuccessToast } from '../../util/toast';
import { ListingType, TRANSACTION_PROCESS_ALIAS, UNIT_TYPE } from '../../util/enums';
import { EDIT, PERKS } from '../../util/constants';
import { addPerksToAdminAccount } from '../../util/api';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import moment from 'moment';
const { UUID } = sdkTypes;

// ================ Action types ================ //

export const CREATE_PERKS_LISTING_REQUEST = 'app/PerksPostPage/CREATE_PERKS_LISTING_REQUEST';
export const CREATE_PERKS_LISTING_SUCCESS = 'app/PerksPostPage/CREATE_PERKS_LISTING_SUCCESS';
export const CREATE_PERKS_LISTING_ERROR = 'app/PerksPostPage/CREATE_PERKS_LISTING_ERROR';

export const SHOW_LISTING_REQUEST = 'app/PerksPostPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/PerksPostPage/SHOW_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  createPerksListingInProgress: false,
  createPerksListingError: null,
  id: null,
  showListingError: null,
};

const PerksPostPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CREATE_PERKS_LISTING_REQUEST:
      return { ...state, createPerksListingInProgress: true, createPerksListingError: null };
    case CREATE_PERKS_LISTING_SUCCESS:
      return { ...state, createPerksListingInProgress: false, createPerksListingError: null };
    case CREATE_PERKS_LISTING_ERROR:
      return { ...state, createPerksListingInProgress: false, createPerksListingError: payload };
    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };
    default:
      return state;
  }
};

export default PerksPostPageReducer;

// ================ Selectors ================ //
export const createPerksListingSelector = state => {
  const { createPerksListingInProgress, createPerksListingError } = state.PerksPostPage;
  return {
    createPerksListingInProgress,
    createPerksListingError,
  };
};

// ================ Action creators ================ //

export const createPerksListingRequest = () => ({ type: CREATE_PERKS_LISTING_REQUEST });
export const createPerksListingSuccess = () => ({
  type: CREATE_PERKS_LISTING_SUCCESS,
});
export const createPerksListingError = error => ({
  type: CREATE_PERKS_LISTING_ERROR,
  error: true,
  payload: error,
});
export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const createPerksListing = params => async (dispatch, getState, sdk) => {
  dispatch(createPerksListingRequest());
  try {
    const {
      title,
      description,
      count,
      userType,
      preferredCompletionDate,
      parentListingTitle,
      parentListingId,
      ...rest
    } = params || {};
    // Calculate adminPerkCount: every 11th perk goes to the admin
    const adminPerkCount = Math.ceil(count / 11);
    const updatedValues = {
      title,
      description,
      publicData: {
        transactionProcessAlias: TRANSACTION_PROCESS_ALIAS.DEFAULT_PURCHASE,
        unitType: UNIT_TYPE.ITEM,
        listingType: ListingType.PURCHASE,
        preferredCompletionDate: moment(preferredCompletionDate).unix(),
        listingCategory: `${userType}${PERKS}`,
        count,
        ...(parentListingId && { parentListingId }),
        ...(parentListingTitle && { parentListingTitle }),
        ...rest,
      },
    };
    if (params.id) {
      updatedValues.id = new UUID(params.id);
    }
    const response = params.id
      ? await sdk.ownListings.update(updatedValues, { expand: true })
      : await sdk.ownListings.create(updatedValues, { expand: true });
    const listing = response?.data?.data;
    const listingTitle = listing?.attributes?.title;
    const listingId = listing?.id?.uuid;
    const adminPerksDetails = {
      listingId,
      listingTitle,
      perksQuantity: adminPerkCount,
    };
    if (listingId) {
      await sdk.stockAdjustments.create(
        {
          listingId,
          quantity: count,
        },
        {
          expand: true,
          include: ['ownListing.currentStock'],
        }
      );
      await addPerksToAdminAccount({
        adminPerksDetails: [adminPerksDetails],
      });
    }
    dispatch(createPerksListingSuccess());
    showSuccessToast('Perks created successfully');
    return listing;
  } catch (error) {
    dispatch(createPerksListingError(storableError(error)));
    showErrorToast('Perks creation failed');
  }
};
export const showListing = (listingId, config) => async (dispatch, getState, sdk) => {
  // Destructure layout config with defaults
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Dispatch initial actions
  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());

  // Build the query parameters
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'author.publicData', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      // Social media
      'variants.facebook',
      'variants.twitter',
      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  try {
    const data = await sdk.ownListings.show(params);
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };
    dispatch(addMarketplaceEntities(data, sanitizeConfig));
    return data;
  } catch (error) {
    dispatch(showListingError(storableError(error)));
  }
};
export const loadData = (params, search, config) => dispatch => {
  const listingId = new UUID(params.id);
  const type = params?.type;
  if (type === EDIT) {
    dispatch(showListing(listingId, config));
  }
  return dispatch(fetchCurrentUser());
};
